import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect } from "react";
import mermaid from "mermaid";
export const MermaidDiagram = ({ code, onFullscreen, totalInteractions }) => {
    const containerRef = useRef(null);
    const [renderState, setRenderState] = useState("loading");
    const [content, setContent] = useState("");
    const renderDiagram = useCallback(async () => {
        if (!code)
            return;
        setRenderState("loading");
        try {
            mermaid.initialize({
                startOnLoad: false,
                theme: "default",
                securityLevel: "loose",
                maxTextSize: 90000,
                maxEdges: 500,
                flowchart: {
                    htmlLabels: false,
                    curve: "basis",
                    padding: 15,
                    nodeSpacing: 50,
                    rankSpacing: 50,
                    diagramPadding: 20,
                },
                themeVariables: {
                    fontFamily: "Arial, sans-serif",
                    fontSize: "12px",
                },
            });
            const diagramId = `mermaid-diagram-${Date.now()}`;
            const { svg } = await mermaid.render(diagramId, code);
            setContent(svg);
            setRenderState("success");
        }
        catch (error) {
            console.error("Mermaid rendering error:", error);
            const lines = code.split("\n").filter((line) => line.trim() && !line.includes("%%"));
            const simpleView = lines
                .slice(1)
                .map((line) => {
                if (line.includes("-->")) {
                    return `<div style="margin: 8px 0; padding: 8px; background: #f8f9fa; border-left: 3px solid #28a745; font-family: monospace;">${line.trim()}</div>`;
                }
                else if (line.includes("[") && line.includes("]")) {
                    return `<div style="margin: 8px 0; padding: 8px; background: #e7f3ff; border-left: 3px solid #374B94; font-weight: bold;">${line.trim()}</div>`;
                }
                return `<div style="margin: 4px 0; color: #666; font-family: monospace;">${line.trim()}</div>`;
            })
                .join("");
            const fallbackContent = `
          <div style="padding: 20px; border: 1px solid #e9ecef; background-color: #f8f9fa; border-radius: 6px;">
            <div style="text-align: center; margin-bottom: 16px; color: #856404; background: #fff3cd; padding: 8px; border-radius: 4px;">
              <strong>📊 Simplified Flow View</strong><br/>
              <small>Interactive diagram temporarily unavailable - showing text representation</small>
            </div>
            <div style="max-height: 400px; overflow-y: auto;">
              ${simpleView}
            </div>
          </div>
        `;
            setContent(fallbackContent);
            setRenderState("fallback");
        }
    }, [code]);
    useEffect(() => {
        renderDiagram();
    }, [renderDiagram]);
    useEffect(() => {
        if (containerRef.current && content && renderState !== "loading") {
            while (containerRef.current.firstChild) {
                containerRef.current.removeChild(containerRef.current.firstChild);
            }
            const wrapper = document.createElement("div");
            wrapper.innerHTML = content;
            while (wrapper.firstChild) {
                containerRef.current.appendChild(wrapper.firstChild);
            }
        }
    }, [content, renderState]);
    const [zoomLevel, setZoomLevel] = useState(0.85);
    return (_jsxs("div", { className: "w-100 h-100 border border-gray-300 rounded-8 bg-white position-relative", children: [_jsxs("div", { style: {
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    zIndex: 10,
                    display: "flex",
                    gap: "8px",
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "8px",
                    borderRadius: "6px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }, children: [_jsx("button", { onClick: () => setZoomLevel((prev) => Math.min(prev + 0.1, 3.0)), style: {
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            background: "white",
                            cursor: "pointer",
                            fontSize: "12px",
                        }, title: "Zoom In", children: "\uD83D\uDD0D+" }), _jsx("button", { onClick: () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.2)), style: {
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            background: "white",
                            cursor: "pointer",
                            fontSize: "12px",
                        }, title: "Zoom Out", children: "\uD83D\uDD0D-" }), _jsx("button", { onClick: () => setZoomLevel(0.85), style: {
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            background: "white",
                            cursor: "pointer",
                            fontSize: "12px",
                        }, title: "Reset Zoom", children: "\u21BB" }), _jsx("button", { onClick: onFullscreen, style: {
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            background: "white",
                            cursor: "pointer",
                            fontSize: "12px",
                        }, title: "Maximize", children: "\u26F6" })] }), _jsx("div", { style: { width: "100%", height: "100%", overflow: "auto" }, children: renderState === "loading" ? (_jsxs("div", { style: {
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "var(--ps-gray)",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }, children: [_jsx("div", { className: "mb-10 fs-14", children: "\uD83D\uDD04 Generating flow diagram..." }), _jsxs("div", { className: "fs-12", children: ["Analyzing ", totalInteractions || 0, " interactions"] })] })) : (_jsx("div", { ref: containerRef, style: {
                        width: `${100}%`,
                        height: `${100}%`,
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: "top left",
                        minHeight: "800px",
                    } })) })] }));
};
//# sourceMappingURL=MermaidDiagram.js.map